/* eslint-disable no-unused-vars */
/* eslint-disable react/no-unstable-nested-components */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Grid,
  IconButton,
  Snackbar,
  SnackbarContent,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  SmartToy as SmartToyIcon,
  ContentCopy,
  CheckCircle,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { Box } from '@mui/system';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import variants from 'modules/common/theme/variants';
import {
  selectAppTheme,
  selectCurrentUrl,
  selectEncodedTabList,
  selectHotelDropdownList,
  selectHotelName,
  selectIsDemoModeActivated,
  selectIsDemoUser,
  selectTabChangeState,
} from 'modules/dashboard/selectors';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { selectSummaryUrl } from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectRegionalMapUrl,
  selectIsFocusOnSet as selectIsReginalMapFocusOnSet,
} from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import {
  selectTrendTimeUrl,
  selectIsFocusOnSet as selectIsTrendFocusOnSet,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import { selectWorkbookUrl } from 'modules/dashboard/components/tab-container/workbook-report/selectors';
import {
  selectIsFocusOnSet as selectIsPaceFocusOnSet,
  selectPaceWidgetShareUrl,
} from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import useApplyCommandUrl, {
  COMMAND_URL_STAGE,
} from 'modules/common/ui-controller/hooks/use-apply-command-url';
import { decodedTabs } from 'modules/common/ui-controller/utils/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import createHotelNameAnonymizer from 'modules/common/utils/anonymize-hotel-names';
import Modal from 'modules/common/components/modal';
import AnimatedText from './hooks/typewriter';
import VoteButtons from './vote-buttons';
import AudioPlayer from './audio-player';
import {
  extractUrlTailObjects,
  parseQueryString,
  getKeyByValue,
  parseAiReply,
  toChatMessage,
} from './chat-util';
import './styles.css';
import { customChatActions } from './slice';
import {
  selectChatLoading,
  selectChatResponse,
  selectChatError,
  selectChatHistory,
  selectChatNextCursor,
  selectChatHistoryLoading,
  selectChatHistoryLoaded,
  selectClearingChatHistory,
} from './selectors';

// thought text shown while each stage of a command url is applied
const STAGE_THOUGHTS = {
  [COMMAND_URL_STAGE.TAB]: '> Changing Tab',
  [COMMAND_URL_STAGE.HOTEL]: '> Changing Hotel',
  [COMMAND_URL_STAGE.HOTEL_GROUP]: '> Changing Hotel Group',
  [COMMAND_URL_STAGE.FILTERS]: '> Changing Filters',
};

const CustomChatView = ({ isOpen, setOpen, tabValue }) => {
  const dispatch = useDispatch();
  const chatLoading = useSelector(selectChatLoading);
  const chatResponse = useSelector(selectChatResponse);
  const chatError = useSelector(selectChatError);
  const chatPending = chatLoading || !!chatResponse || !!chatError;
  const chatHistory = useSelector(selectChatHistory);
  const historyNextCursor = useSelector(selectChatNextCursor);
  const historyLoading = useSelector(selectChatHistoryLoading);
  const historyLoaded = useSelector(selectChatHistoryLoaded);
  const clearingHistory = useSelector(selectClearingChatHistory);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  //
  const currentUrl = useSelector(selectCurrentUrl);
  const summaryFilter = useSelector(selectSummaryUrl);
  const regionalMapFilter = useSelector(selectRegionalMapUrl);
  const trendTimeFilter = useSelector(selectTrendTimeUrl);
  const workbookFilter = useSelector(selectWorkbookUrl);
  const paceFilter = useSelector(selectPaceWidgetShareUrl);
  const encodedTabs = useSelector(selectEncodedTabList);
  // focusOn set
  const isPaceFocusOnSet = useSelector(selectIsPaceFocusOnSet);
  const isTrendFocusOnSet = useSelector(selectIsTrendFocusOnSet);
  const isReginalMapFocusOnSet = useSelector(selectIsReginalMapFocusOnSet);
  //
  const currentTab = encodedTabs[tabValue];
  const cb = decodedTabs[currentTab];
  const [queryString, setQueryString] = useState({
    value: currentUrl,
    tab: cb,
    vers: 0,
  });
  // base urls
  const basedUrls = useMemo(
    () => ({
      sm: summaryFilter,
      wb: workbookFilter,
      rm: regionalMapFilter,
      td: trendTimeFilter,
      pa: paceFilter,
    }),
    [summaryFilter, workbookFilter, regionalMapFilter, paceFilter, trendTimeFilter]
  );
  // focus on status of each tab
  const isFocusOnSet = useMemo(
    () => ({
      sm: true,
      wb: true,
      rm: isReginalMapFocusOnSet,
      td: isTrendFocusOnSet,
      pa: isPaceFocusOnSet,
    }),
    [isPaceFocusOnSet, isReginalMapFocusOnSet, isTrendFocusOnSet]
  );

  const themeName = useSelector(selectAppTheme);
  const currentUser = useSelector(selectAuthUser);
  const hotelList = useSelector(selectHotelDropdownList);
  const currentHotel = useSelector(selectHotelName);
  const tabChange = useSelector(selectTabChangeState);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [latestMessage, setLatestMessage] = useState('');
  const [showSql, setShowSql] = useState(false); // Add state for SQL toggle
  const [enableHelp, setEnableHelp] = useState(true); // Add state for help toggle
  const [enableGenie, setEnableGenie] = useState(false); // Add state for Genie toggle
  const [enableUseBM25, setEnableUseBM25] = useState(false); // Add state for Genie toggle
  const [thoughtText, setThoughtText] = useState('');
  const [timer, setTimer] = useState(null);
  const [newParamSet, setNewParamSet] = useState(null);
  const [isTourOn, setIsTourOn] = useState(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.name === themeName),
    [themeName]
  );

  const scrollRef = useRef(null);
  // scroll height before an older page is prepended, used to keep the user's scroll position
  const prevScrollHeightRef = useRef(null);

  // Replaces the actual hotel names in the messages with the demo mode hotel labels
  const anonymize = useMemo(
    () =>
      isDemoUser || isDemoModeActivated ? createHotelNameAnonymizer(hotelList, isDemoUser) : null,
    [hotelList, isDemoUser, isDemoModeActivated]
  );

  // saved history followed by the messages of the current session
  const displayMessages = useMemo(() => {
    const allMessages = [...chatHistory.map(toChatMessage), ...messages];
    if (!anonymize) return allMessages;
    return allMessages.map((msg) => ({
      ...msg,
      text: anonymize(msg.text),
      ...(msg.tourSteps && {
        tourSteps: msg.tourSteps.map((step) => ({
          ...step,
          content: anonymize(String(step.content)),
        })),
      }),
    }));
  }, [chatHistory, messages, anonymize]);

  const toggleSql = () => {
    setShowSql((prev) => !prev);
  };

  const toggleEnableGenie = () => {
    setEnableGenie((prev) => !prev);
  };

  const toggleEnableUseBM25 = () => {
    setEnableUseBM25((prev) => !prev);
  };

  const toggleHelp = () => {
    setEnableHelp((prev) => !prev);
  };

  useEffect(() => {
    setQueryString({
      value: currentUrl,
      tab: decodedTabs[encodedTabs[tabValue]],
      vers: 0,
    });
  }, [currentUrl, tabValue]);

  const { applyTab: executeTab, applySearch: applyFilter } = useApplyCommandUrl({
    ready: isFocusOnSet[decodedTabs[encodedTabs[tabValue]]],
    delay: 6000,
    onStage: (stage) => setThoughtText(STAGE_THOUGHTS[stage]),
    onDone: () => {
      setNewParamSet(null);
      setThoughtText('');
    },
  });

  const applyParamsToUrl = (base, newParams) => {
    const baseParams = parseQueryString(base);
    const mergedParams = { ...baseParams, ...newParams };
    const mergedUrl = Object.entries(mergedParams)
      .map(([key, value]) => `${key}=${value.trim()}`)
      .join('&');
    return mergedUrl;
  };

  useEffect(() => {
    if (tabChange && isFocusOnSet[decodedTabs[encodedTabs[tabValue]]]) {
      setQueryString({
        ...queryString,
        value: basedUrls[decodedTabs[encodedTabs[tabValue]]],
      });
    }
  }, [tabChange, isFocusOnSet]);

  useEffect(() => {
    if (tabChange && queryString.value && newParamSet !== null) {
      const finalUrl = applyParamsToUrl(queryString.value, newParamSet);
      applyFilter(finalUrl);
    }
  }, [tabChange, queryString.value, newParamSet, applyFilter]);

  const driveUI = (lastMessage) => {
    const urlQuery = extractUrlTailObjects(lastMessage);
    if (!urlQuery) return;
    const newParams = parseQueryString(urlQuery);
    const { tab } = newParams;
    const gotTab = 'tab' in newParams ? tab?.trim() : queryString.tab;

    const urlTab = gotTab
      ? getKeyByValue(decodedTabs, gotTab)
      : getKeyByValue(decodedTabs, queryString.tab);

    setQueryString({
      ...queryString,
      value: '',
      tab: gotTab
        ? getKeyByValue(decodedTabs, gotTab)
        : getKeyByValue(decodedTabs, queryString.tab),
    });

    if ('tab' in newParams) {
      delete newParams.tab;
    }
    executeTab(urlTab);
    setNewParamSet(newParams);
  };

  function sendMessage() {
    if (chatPending) return;
    if (input.trim() !== '') {
      const userMessage = input.trim();
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'Human', text: userMessage, id: `User-${Date.now()}` },
        { role: 'AI', text: '', id: `AI-${Date.now()}` },
      ]);

      const payload = {
        type: enableHelp ? 'help' : 'message',
        data: { message: userMessage, role: 'human' },
        ...(!enableHelp && {
          userData: {
            userId: currentUser?.id,
            organizationId: currentUser?.organizationId,
          },
        }),
        other: {
          prev_url_tail: enableHelp
            ? `${queryString.value}&tab=${queryString.tab}`
            : queryString.value,
          current_tab: queryString.tab,
          hotel_names: hotelList?.map((hotel) => ({
            id: hotel?.id,
            code: hotel?.hotelCode,
            databricksId: hotel?.databricksId,
            name: hotel?.name,
          })),
          isGenieEnabled: enableGenie,
          isUseBM25Enabled: enableUseBM25,
          selected_hotel_name: currentHotel?.name,
          current_date: new Date().toISOString(),
          use_nl_to_sql: showSql,
        },
      };

      dispatch(customChatActions.sendMessage(payload));

      setInput('');
      setThoughtText('Thinking...');
    }
  }

  function updateMessages(message, isTourIncluded = false, url = '', steps = [], tab = '') {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const lastMessage = updatedMessages[updatedMessages.length - 1];
      if (lastMessage?.role === 'AI') {
        lastMessage.text += `${message}`;
        if (isTourIncluded) {
          lastMessage.url = url;
          lastMessage.tourSteps = steps;
          lastMessage.targetTab = tab;
        }
      } else {
        updatedMessages.push({
          role: 'AI',
          text: message,
          id: `AI-${Date.now()}`,
          ...(isTourIncluded && {
            url,
            tourSteps: steps,
            targetTab: tab,
          }),
        });
      }
      setThoughtText('');

      return updatedMessages;
    });
  }

  function handleChatMessage(message) {
    const message1 = message?.data?.message;

    if (message.type === 'message') {
      if (message1) {
        if (message1?.includes('URL_TAIL==')) {
          setThoughtText('Adjusting View');
          setLatestMessage(message1);
          updateMessages(parseAiReply('message', message1).text);
          // eslint-disable-next-line no-useless-return
          return;
        }
        // Handling Normal Case
        updateMessages(message1);
      }
      setThoughtText('');
    } else if (message.type === 'help') {
      if (message1) {
        const { text, url, tourSteps, targetTab } = parseAiReply('help', message1);
        updateMessages(text, !!tourSteps, url, tourSteps, targetTab);
      } else {
        updateMessages('Something went wrong. Please try again');
        return;
      }
      setThoughtText('');
    } else if (message.type === 'graph_end') {
      console.log('GRAPH END!!!');
      setThoughtText('');
    } else {
      console.log('Received Msc Message:', message);
    }
  }
  useEffect(() => {
    if (!chatResponse && !chatError) return;
    try {
      if (chatError) {
        updateMessages(chatError);
      } else {
        handleChatMessage(chatResponse);
      }
    } catch (error) {
      updateMessages('Unable to display the chat response. Please try again.');
    } finally {
      dispatch(customChatActions.resetChat());
    }
  }, [chatResponse, chatError]);

  useEffect(() => {
    if (latestMessage) {
      driveUI(latestMessage);
    }
  }, [latestMessage]);

  useEffect(() => {
    dispatch(customChatActions.resetChat());
    return () => {
      dispatch(customChatActions.resetChat());
      dispatch(customChatActions.resetChatHistory());
    };
  }, []);

  // Load the latest page of saved messages when the chat opens
  useEffect(() => {
    if (isOpen && !historyLoaded) {
      dispatch(customChatActions.fetchChatHistory({}));
    }
  }, [isOpen, historyLoaded]);

  // The chat content is unmounted while closed, so go back to the latest message on open
  useLayoutEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isOpen]);

  // Keep the scroll position when older messages are prepended, otherwise go to the latest message
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    const { scrollHeight } = scrollRef.current;
    scrollRef.current.scrollTop =
      prevScrollHeightRef.current === null
        ? scrollHeight
        : scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
  }, [chatHistory]);

  const handleMessagesScroll = (event) => {
    const { scrollTop, scrollHeight } = event.currentTarget;
    if (scrollTop < 40 && historyNextCursor && !historyLoading && !clearingHistory) {
      prevScrollHeightRef.current = scrollHeight;
      dispatch(customChatActions.fetchChatHistory({ before: historyNextCursor }));
    }
  };

  const handleClearHistory = () => {
    setIsClearConfirmOpen(false);
    setMessages([]);
    dispatch(customChatActions.clearChatHistory());
  };

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Clear thought text
  useEffect(() => {
    if (timer) clearTimeout(timer);
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    // setTimer(setTimeout(() => setThoughtText(''), 6000));
    return () => clearTimeout(timer);
  }, [thoughtText]);

  // Copy text
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied!', {
        className: 'pill-toast',
        position: 'bottom-right',
        hideProgressBar: true,
        closeButton: false,
        icon: false,
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  //
  useEffect(() => {
    setMessages([]);
    dispatch(dashboardActions.resetTour());
  }, []);
  //
  const startTour = (tab = 'sm', steps = []) => {
    setIsTourOn(true);
    dispatch(dashboardActions.setTourSteps(steps));
    dispatch(dashboardActions.setTourTarget(tab));
  };
  //
  useEffect(() => {
    if (isTourOn) {
      dispatch(dashboardActions.startTour());
      setIsTourOn(false);
    }
  }, [isTourOn]);
  //
  const executeTour = (urlTail) => {
    driveUI(urlTail);
  };
  //
  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={isOpen}
        onClose={(event, reason) => {
          // clicks in the clear history dialog count as click away from the chat
          if (reason === 'clickaway' && event?.target?.closest?.('.MuiDialog-root')) return;
          setOpen(false);
        }}
      >
        <SnackbarContent
          sx={{ backgroundColor: 'transparent', borderRadius: 5 }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              onClick={(reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                dispatch(dashboardActions.resetTourUrl());
                setOpen(false);
              }}
              sx={{ position: 'absolute', right: 30, top: 25, color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          }
          message={
            <Grid
              sx={{
                flexDirection: 'column',
                width: 350,
                paddingBottom: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 5,
                boxShadow: 9,
              }}
            >
              {/* Header */}
              <Grid
                sx={{
                  backgroundColor: selectedVariant?.header?.background,
                  display: 'flex',
                  height: 50,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  alignItems: 'center',
                  justifyContent: 'space-between', // Adjust layout
                  px: 2,
                }}
              >
                <Typography className="assist">Assistant</Typography>
                <IconButton
                  size="small"
                  aria-label="clear chat history"
                  title="Clear history"
                  disabled={chatPending || clearingHistory}
                  onClick={() => setIsClearConfirmOpen(true)}
                  sx={{ color: 'white' }}
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
                {/* Help Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      // className="sql"
                      checked={enableHelp}
                      onChange={toggleHelp}
                      size="small"
                      color="info"
                    />
                  }
                  label={
                    <Typography className="help" variant="body2" sx={{ color: 'white' }}>
                      Help
                    </Typography>
                  }
                  sx={{ color: 'white', mr: 1, marginRight: 6 }} // Adjust margin as needed
                />

                {/* SQL Toggle */}
                <FormControlLabel
                  control={
                    <Switch checked={showSql} onChange={toggleSql} size="small" color="primary" />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      SQL
                    </Typography>
                  }
                  sx={{ color: 'white', mr: 1, marginRight: 7, display: 'none' }} // Adjust margin as needed
                />
              </Grid>

              {/* Messages */}
              <Grid
                ref={scrollRef}
                onScroll={handleMessagesScroll}
                sx={{ width: '100%', height: 350, overflowY: 'scroll', mx: 1 }}
              >
                <Grid container justifyContent="space-between" py={2}>
                  {/* Genie Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableGenie}
                        onChange={toggleEnableGenie}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        Genie
                      </Typography>
                    }
                    sx={{ color: 'black' }}
                  />

                  {/* is BM 25 Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableUseBM25}
                        onChange={toggleEnableUseBM25}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        BM 25
                      </Typography>
                    }
                    sx={{ color: 'black' }}
                  />
                </Grid>
                {historyLoading && (
                  <Grid container justifyContent="center" py={1}>
                    <CircularProgress size={20} thickness={4} />
                  </Grid>
                )}
                {displayMessages.length === 0 ? (
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: '100%' }}
                  >
                    <Typography color="#1f1f1f">{!historyLoading && 'No Messages Yet'}</Typography>
                  </Grid>
                ) : (
                  displayMessages.map((msg, index) => (
                    <Grid
                      key={msg.id}
                      item
                      xs={10}
                      sx={{
                        padding: 1,
                        paddingLeft: 2,
                        borderRadius: 2,
                        width: '100%',
                        mt: 1,
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* Avatar */}
                      <Box
                        sx={{
                          backgroundColor: '#3F8CFC',
                          width: 25,
                          height: 25,
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {msg.role === 'AI' ? <SmartToyIcon /> : <AccountCircleIcon />}
                      </Box>

                      {/* Message Content */}
                      <Grid sx={{ color: '#1f1f1f' }}>
                        <AnimatePresence mode="wait">
                          <motion.div
                            // key={msg.text}
                            key={`${msg.id}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                          >
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: (props) => (
                                  <p style={{ paddingTop: 10, margin: 0 }} {...props} />
                                ), // eslint-disable-line
                                code: (props) => {
                                  // eslint-disable-line
                                  const { inline, children } = props;

                                  if (inline) {
                                    // Inline code (`)
                                    return (
                                      <code
                                        style={{
                                          backgroundColor: '#e5eaed',
                                          padding: '2px 4px',
                                          borderRadius: '4px',
                                          fontSize: '0.9em',
                                        }}
                                      >
                                        {children}
                                      </code>
                                    );
                                  }

                                  // Block code (```)

                                  const preRef = useRef(null);
                                  const [clickedCopy, setClickedCopy] = useState(false);

                                  // eslint-disable-next-line consistent-return
                                  useEffect(() => {
                                    // eslint-disable-line
                                    if (clickedCopy) {
                                      const timerObj = setTimeout(() => {
                                        setClickedCopy(false);
                                      }, 500);

                                      return () => clearTimeout(timerObj);
                                    }
                                  }, [clickedCopy]);

                                  const handleCopy = () => {
                                    if (preRef.current) {
                                      navigator.clipboard
                                        .writeText(preRef.current.innerText)
                                        .then(() => {
                                          setClickedCopy(true);
                                        });
                                    }
                                  };

                                  return (
                                    <details
                                      style={{
                                        backgroundColor: '#2e384a',
                                        color: 'white',
                                        padding: '8px 0px',
                                        width: '100%',
                                        minHeight: '25px',
                                        borderRadius: '8px',
                                        marginTop: '8px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                      }}
                                      onToggle={(e) => {
                                        const pre = e.target.querySelector('pre');
                                        if (e.target.open) {
                                          pre.style.opacity = '1';
                                          pre.style.maxHeight = '500px';
                                        } else {
                                          pre.style.opacity = '0';
                                          pre.style.maxHeight = '0px';
                                        }
                                      }}
                                    >
                                      <summary // eslint-disable-line
                                        style={{
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          paddingLeft: '10px',
                                          transition: 'color 0.2s ease-in-out',
                                        }}
                                        onMouseOver={(e) => (e.target.style.color = '#ddd')} // eslint-disable-line
                                        onMouseOut={(e) => (e.target.style.color = 'white')} // eslint-disable-line
                                      >
                                        <span style={{ paddingLeft: '5px', fontSize: '12px' }}>
                                          Show Logic
                                        </span>
                                      </summary>
                                      <div style={{ position: 'relative' }}>
                                        <button // eslint-disable-line
                                          onClick={handleCopy}
                                          style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '5px',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'white',
                                          }}
                                          title="Copy to clipboard"
                                        >
                                          {clickedCopy ? (
                                            <CheckCircle style={{ color: 'greenyellow' }} />
                                          ) : (
                                            <ContentCopy />
                                          )}
                                        </button>
                                        <pre
                                          ref={preRef}
                                          style={{
                                            padding: '8px',
                                            overflowX: 'auto',
                                            backgroundColor: '#252f3e',
                                            margin: '5px 0 0 0',
                                          }}
                                        >
                                          {children}
                                        </pre>
                                      </div>
                                    </details>
                                  );
                                },
                                table: (
                                  props // eslint-disable-line
                                ) => (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table
                                      style={{
                                        width: 'auto',
                                        borderCollapse: 'collapse',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '5px',
                                      }}
                                      {...props}
                                    />
                                  </div>
                                ),
                                th: (
                                  { isHeader, ...props } // eslint-disable-line
                                ) => (
                                  <th
                                    style={{
                                      border: '1px solid #d1d5db',
                                      borderRadius: '5px',
                                      padding: '8px 16px',
                                      textAlign: 'left',
                                    }}
                                    {...props}
                                  />
                                ),
                                td: ({ isHeader, ...props }) => (
                                  <td
                                    style={{
                                      border: '1px solid #d1d5db',
                                      borderRadius: '5px',
                                      padding: '8px 16px',
                                      textAlign: 'left',
                                    }}
                                    {...props}
                                  />
                                ),
                              }}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          </motion.div>
                        </AnimatePresence>
                      </Grid>

                      {/* Thought Text */}
                      {index === displayMessages.length - 1 && thoughtText && (
                        <Box sx={{ pt: 1 }}>
                          <AnimatedText value={thoughtText} />
                        </Box>
                      )}
                      {/* Tour */}

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          marginTop: '10px',
                        }}
                      >
                        {msg.role === 'AI' && msg?.tourSteps?.length > 0 && (
                          <Button
                            variant="contained"
                            onClick={() => startTour(msg?.targetTab, msg?.tourSteps)}
                          >
                            Start Tour
                          </Button>
                        )}
                        {msg.role === 'AI' && msg?.url && (
                          <Button
                            sx={{ ml: 2 }}
                            variant="contained"
                            onClick={() => executeTour(msg?.url)}
                          >
                            Execute Tour
                          </Button>
                        )}
                      </div>

                      {/* Vote Buttons */}

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          marginTop: '10px',
                        }}
                      >
                        {msg.role === 'AI' && msg.text && <VoteButtons />}
                        {msg.role === 'AI' && msg.text && <AudioPlayer inputText={msg.text} />}
                        {msg.role === 'AI' && msg.text && (
                          <IconButton
                            onClick={() => copyText(msg.text)}
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#9b9b9b',
                              width: 25,
                              height: 25,
                              borderRadius: '5px',
                              '&:hover': { backgroundColor: '#eee' },
                              border: '1px solid #e0e0e0',
                              fontSize: '15px',
                              padding: '0',
                              margin: '0',
                              marginLeft: '5px',
                            }}
                          >
                            <ContentCopy sx={{ fontSize: '18px' }} />
                          </IconButton>
                        )}
                      </div>
                    </Grid>
                  ))
                )}
              </Grid>

              {/* Input Field */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  padding: '5px 10px',
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Send a message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '5px',
                    '& fieldset': { border: 'none' },
                    padding: '0px',
                    width: '82%',
                  }}
                />

                <IconButton
                  type="submit"
                  disabled={chatPending || !input.trim()}
                  sx={{
                    position: 'absolute',
                    right: 25,
                    backgroundColor: '#3F8CFC',
                    color: '#fff',
                    width: 35,
                    height: 35,
                    borderRadius: '5px',
                    '&:hover': { backgroundColor: '#357AE8' },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </form>
            </Grid>
          }
        />
      </Snackbar>
      <Modal
        open={isClearConfirmOpen}
        handleClose={() => setIsClearConfirmOpen(false)}
        title="Clear chat history"
        content="This removes your saved chat history, including SMS and WhatsApp messages. Do you want to continue?"
        handleSuccess={handleClearHistory}
        closeLabel="Cancel"
        successLabel="Clear"
        color="error"
        variant="contained"
      />
    </>
  );
};

export default CustomChatView;
